import api from './api';

export const publicDirectoryService = {
  searchFacilityDevices: async (search) => {
    const response = await api.get('/public/facility-devices', {
      params: { search },
    });
    return response.data;
  },
};

// Equipment API calls
export const equipmentService = {
  /**
   * Get all equipment
   */
  getAll: async ({ page = 1, page_size = 20, facility_id, facility_name, lifecycle_state, operational_status, search, linked } = {}) => {
    const response = await api.get('/equipment', {
      params: { page, page_size, facility_id, facility_name, lifecycle_state, operational_status, search, linked }
    });
    return response.data;
  },

  /**
   * Get equipment by ID
   */
  getById: async (id) => {
    const response = await api.get(`/equipment/${id}`);
    return response.data;
  },

  /**
   * Get equipment dashboard summary (requests, worklists, gateway link)
   */
  getSummary: async (id) => {
    const response = await api.get(`/equipment/${id}/summary`);
    return response.data;
  },

  /**
   * Republish a single equipment modality into Orthanc.
   */
  publishToOrthanc: async (id) => {
    const response = await api.post(`/equipment/${id}/publish-orthanc`);
    return response.data;
  },

  /**
   * Register machines in Orthanc under their linked AE title. Without ids, every linked machine.
   * onlyMissing skips machines Orthanc already has with the same AE title, host and port.
   */
  publishToOrthancBatch: async ({ equipmentIds, onlyMissing = true } = {}) => {
    const response = await api.post('/equipment/publish-orthanc/batch', {
      equipment_ids: equipmentIds?.length ? equipmentIds : undefined,
      only_missing: onlyMissing,
    });
    return response.data;
  },

  /**
   * Create new equipment
   */
  create: async (data) => {
    const response = await api.post('/equipment', data);
    return response.data;
  },

  /**
   * Update equipment
   */
  update: async (id, data) => {
    const response = await api.put(`/equipment/${id}`, data);
    return response.data;
  },

  /**
   * Delete equipment
   */
  delete: async (id) => {
    const response = await api.delete(`/equipment/${id}`);
    return response.data;
  },

  /**
   * Get equipment capabilities
   */
  getCapabilities: async (id) => {
    const response = await api.get(`/equipment/${id}/capabilities`);
    return response.data;
  },

  /**
   * Get equipment procedures
   */
  getProcedures: async (id) => {
    const response = await api.get(`/equipment/${id}/procedures`);
    return response.data;
  },

  /**
   * Get operational equipment at facility
   */
  getOperational: async (facilityId) => {
    const response = await api.get(`/equipment/facility/${facilityId}/operational`);
    return response.data;
  },

  /**
   * Add a procedure mapping to equipment
   */
  addProcedure: async (equipmentId, data) => {
    const response = await api.post(`/equipment/${equipmentId}/procedures`, data);
    return response.data;
  },

  /**
   * Remove a procedure mapping from equipment
   */
  removeProcedure: async (equipmentId, procedureId) => {
    const response = await api.delete(`/equipment/${equipmentId}/procedures/${procedureId}`);
    return response.data;
  },

  /**
   * Get pending ping requests awaiting approval.
   */
  getPendingPingRequests: async ({ page = 1, page_size = 50, request_type } = {}) => {
    const response = await api.get('/dicom/events/ping-events', {
      params: { page, page_size, request_type },
    });
    return response.data;
  },

  /**
   * Poll the realtime ping approval queue.
   */
  getRealtimePingRequests: async ({ since } = {}) => {
    const response = await api.get('/equipment/ping-requests/realtime', {
      params: { since },
    });
    return response.data;
  },

  /**
   * Get IDs of equipment already linked to ping requests.
   */
  getLinkedPingEquipmentIds: async () => {
    const response = await api.get('/equipment/ping-requests/linked-equipment-ids');
    return response.data;
  },

  /**
   * Approve a ping request.
   */
  approvePingRequest: async (
    id,
    { approval_reason = '', equipment_id, ae_title_source, selected_ae_title } = {},
  ) => {
    const response = await api.post(`/equipment/ping-requests/${id}/approve`, {
      approval_reason,
      equipment_id,
      ae_title_source,
      selected_ae_title,
    });
    return response.data;
  },

  /**
   * Reject a ping request.
   */
  rejectPingRequest: async (id, approval_reason = '') => {
    const response = await api.post(`/equipment/ping-requests/${id}/reject`, { approval_reason });
    return response.data;
  },

  /**
   * Unlink a machine AE title (active link) from its equipment.
   */
  unlinkPingRequest: async (id, reason = '') => {
    const response = await api.post(`/equipment/ping-requests/${id}/unlink`, { reason });
    return response.data;
  },

  /**
   * Online status, current link, IP history and recent echo checks.
   */
  getConnection: async (id) => {
    const response = await api.get(`/equipment/${id}/connection`);
    return response.data;
  },

  /**
   * Recent MWL C-FIND queries from this machine and what they returned.
   */
  getWorklistQueries: async (id, { page = 1, page_size = 10, returned } = {}) => {
    const response = await api.get(`/equipment/${id}/worklist-queries`, {
      params: { page, page_size, returned },
    });
    return response.data;
  },

  /**
   * Ping the machine with a DICOM C-ECHO from Orthanc; overrides are optional.
   */
  echo: async (id, { ae_title, host, port } = {}) => {
    const response = await api.post(`/equipment/${id}/echo`, {
      ae_title: ae_title || undefined,
      host: host || undefined,
      port: port ? Number(port) : undefined,
    });
    return response.data;
  },
};

// Vendor API calls
export const vendorService = {
  /**
   * Get all vendors
   */
  getAll: async ({ page = 1, page_size = 20, lifecycle_state, search } = {}) => {
    const response = await api.get('/vendors', {
      params: { page, page_size, lifecycle_state, search }
    });
    return response.data;
  },

  /**
   * Get every vendor by walking the paginated API.
   */
  getAllPages: async ({ lifecycle_state, search } = {}) => {
    const pageSize = 100;
    let currentPage = 1;
    let total = 0;
    const allItems = [];

    do {
      const data = await vendorService.getAll({
        page: currentPage,
        page_size: pageSize,
        lifecycle_state,
        search,
      });
      const pageItems = data.items || [];
      total = data.total || 0;
      allItems.push(...pageItems);
      currentPage += 1;
    } while (allItems.length < total);

    return allItems;
  },

  /**
   * Get vendor by ID
   */
  getById: async (id) => {
    const response = await api.get(`/vendors/${id}`);
    return response.data;
  },

  /**
   * Get vendor dashboard summary (machines by facility and headline counts)
   */
  getSummary: async (id) => {
    const response = await api.get(`/vendors/${id}/summary`);
    return response.data;
  },

  /**
   * Create new vendor
   */
  create: async (data) => {
    const response = await api.post('/vendors', data);
    return response.data;
  },

  /**
   * Update vendor
   */
  update: async (id, data) => {
    const response = await api.put(`/vendors/${id}`, data);
    return response.data;
  },

  /**
   * Delete vendor
   */
  delete: async (id) => {
    const response = await api.delete(`/vendors/${id}`);
    return response.data;
  },

  /**
   * Get vendor contacts
   */
  getContacts: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/contacts`);
    return response.data;
  },

  /**
   * List dropdown config entries.
   */
  getDropdownConfig: async ({ category, status } = {}) => {
    const response = await api.get('/vendors/dropdown-config', {
      params: { category, status },
    });
    return response.data;
  },

  /**
   * Create a dropdown config entry.
   */
  createDropdownConfig: async (data) => {
    const response = await api.post('/vendors/dropdown-config', data);
    return response.data;
  },

  /**
   * Update a dropdown config entry.
   */
  updateDropdownConfig: async (id, data) => {
    const response = await api.put(`/vendors/dropdown-config/${id}`, data);
    return response.data;
  },

  /**
   * Backward-compatible modality catalog helper.
   */
  getModalities: async ({ status } = {}) => {
    return vendorService.getDropdownConfig({ category: 'modality', status });
  },

  /**
   * Backward-compatible modality create helper.
   */
  createModality: async (data) => {
    return vendorService.createDropdownConfig({ ...data, category: 'modality' });
  },

  /**
   * Backward-compatible modality update helper.
   */
  updateModality: async (id, data) => {
    return vendorService.updateDropdownConfig(id, { ...data, category: 'modality' });
  },

  /**
   * List modalities linked to a vendor.
   */
  getVendorModalities: async (vendorId) => {
    const response = await api.get(`/vendors/${vendorId}/modalities`);
    return response.data;
  },

  /**
   * Add vendor modality associations.
   */
  createVendorModalities: async (vendorId, modality_ids) => {
    const response = await api.post(`/vendors/${vendorId}/modalities`, { modality_ids });
    return response.data;
  },

  /**
   * Replace vendor modality associations.
   */
  updateVendorModalities: async (vendorId, modality_ids) => {
    const response = await api.put(`/vendors/${vendorId}/modalities`, { modality_ids });
    return response.data;
  },
};

// Procedure API calls
export const procedureService = {
  /**
   * Get all procedures
   */
  getAll: async (skip = 0, limit = 100) => {
    const response = await api.get('/procedures', {
      params: { skip, limit }
    });
    return response.data;
  },

  /**
   * Get procedure by ID
   */
  getById: async (id) => {
    const response = await api.get(`/procedures/${id}`);
    return response.data;
  },

  /**
   * Get procedure by code
   */
  getByCode: async (code) => {
    const response = await api.get(`/procedures/code/${code}`);
    return response.data;
  },

  /**
   * Create new procedure
   */
  create: async (data) => {
    const response = await api.post('/procedures', data);
    return response.data;
  },

  /**
   * Update procedure
   */
  update: async (id, data) => {
    const response = await api.put(`/procedures/${id}`, data);
    return response.data;
  },

  /**
   * Delete procedure
   */
  delete: async (id) => {
    const response = await api.delete(`/procedures/${id}`);
    return response.data;
  },

  /**
   * Get active procedures
   */
  getActive: async () => {
    const response = await api.get('/procedures/active/list');
    return response.data;
  },
};

// Modality-Procedure Mapping API calls
export const modalityProcedureMappingService = {
  /**
   * List all modality-procedure mappings, optionally filtered by modality_code
   */
  getAll: async ({ modality_code, modality_id } = {}) => {
    const response = await api.get('/procedures/modality-mappings', {
      params: { modality_code, modality_id },
    });
    return response.data;
  },

  /**
   * Create a new modality-procedure mapping
   */
  create: async (data) => {
    const response = await api.post('/procedures/modality-mappings', data);
    return response.data;
  },

  /**
   * Update a modality-procedure mapping (e.g. its description keywords)
   */
  update: async (mappingId, data) => {
    const response = await api.patch(`/procedures/modality-mappings/${mappingId}`, data);
    return response.data;
  },

  /**
   * Delete a modality-procedure mapping by mapping ID
   */
  delete: async (mappingId) => {
    const response = await api.delete(`/procedures/modality-mappings/${mappingId}`);
    return response.data;
  },
};

// Facility API calls
export const facilityService = {
  /**
   * Get all facilities
   */
  getAll: async ({ page = 1, page_size = 20, county, hmis, status, sha_contract_status, search } = {}) => {
    const response = await api.get('/facilities', {
      params: { page, page_size, county, hmis, status, sha_contract_status, search }
    });
    return response.data;
  },

  /**
   * Get every facility by walking the paginated API.
   */
  getAllPages: async ({ county, hmis, status, sha_contract_status, search } = {}) => {
    const pageSize = 100;
    let currentPage = 1;
    let total = 0;
    const allItems = [];

    do {
      const data = await facilityService.getAll({
        page: currentPage,
        page_size: pageSize,
        county,
        hmis,
        status,
        sha_contract_status,
        search,
      });
      const pageItems = data.items || [];
      total = data.total || 0;
      allItems.push(...pageItems);
      currentPage += 1;
    } while (allItems.length < total);

    return allItems;
  },

  /**
   * Get facility by ID
   */
  getById: async (id) => {
    const response = await api.get(`/facilities/${id}`);
    return response.data;
  },

  /**
   * Get facility dashboard summary (machines, vendors, request counts)
   */
  getSummary: async (id) => {
    const response = await api.get(`/facilities/${id}/summary`);
    return response.data;
  },

  /**
   * Create new facility
   */
  create: async (data) => {
    const response = await api.post('/facilities', data);
    return response.data;
  },

  /**
   * Update facility
   */
  update: async (id, data) => {
    const response = await api.put(`/facilities/${id}`, data);
    return response.data;
  },

  /**
   * Soft delete facility
   */
  delete: async (id) => {
    const response = await api.delete(`/facilities/${id}`);
    return response.data;
  },

  /**
   * Search facility in FR registry (no auto-creation)
   */
  searchFacilityRegistry: async (identifier, identifierType = 'fr-code') => {
    const response = await api.get('/facilities/fr/search', {
      params: { identifier, 'identifier-type': identifierType }
    });
    return response.data;
  },

  /**
   * Onboard facility from FR registry with NESP validation
   */
  onboardFacility: async (data) => {
    const response = await api.post('/facilities/fr/onboard', data);
    return response.data;
  },

  /**
   * Push an active NESP contract for this facility to the FR registry
   */
  pushNespContract: async (id) => {
    const response = await api.post(`/facilities/${id}/fr/nesp-contract`);
    return response.data;
  },
};

// Medical Request API calls
export const requestService = {
  /**
   * Get paginated requests with optional filters
   */
  getAll: async ({ page = 1, page_size = 20, status, patient_id, patient, facility_id, facility_name, vendor_id, has_claim, has_preauth } = {}) => {
    const response = await api.get('/requests', {
      params: { page, page_size, status, patient_id, patient, facility_id, facility_name, vendor_id, has_claim, has_preauth }
    });
    return response.data;
  },

  /**
   * Get request details by UUID
   */
  getById: async (id) => {
    const response = await api.get(`/requests/${id}`);
    return response.data;
  },

  /**
   * Create request via EMR intake endpoint
   */
  intake: async (data) => {
    const response = await api.post('/requests/emr-intake', data);
    return response.data;
  },

  /**
   * Attach or clear claim billing details for an existing request.
   */
  updateClaim: async (data) => {
    const response = await api.post('/requests/claim', data);
    return response.data;
  },

  /**
   * Get callback logs for a request
   */
  getCallbackLogs: async (requestId) => {
    const response = await api.get(`/requests/${requestId}/callback-logs`);
    return response.data;
  },

  /**
   * Get retarget-eligible equipment for a request (facility + procedure capable).
   */
  getRetargetEligibleEquipment: async (requestId, facilityId) => {
    const response = await api.get(`/requests/${requestId}/eligible-equipment`, {
      params: { facility_id: facilityId },
    });
    return response.data;
  },

  /**
   * Retarget request to a new facility and equipment.
   */
  retarget: async (requestId, data) => {
    const response = await api.post(`/requests/${requestId}/retarget`, data);
    return response.data;
  },

  /**
   * Resend and verify an Orthanc worklist for a request.
   */
  /**
   * Pending/failed requests with no worklist in Orthanc (publishing failed).
   */
  getRepublishCandidates: async () => {
    const response = await api.get('/requests/mwl/republish-candidates');
    return response.data;
  },

  /**
   * Republish worklists for the given requests, or for all candidates.
   */
  republishWorklists: async ({ requestIds, allCandidates = false, limit = 200 } = {}) => {
    const response = await api.post('/requests/mwl/republish', {
      request_ids: requestIds && requestIds.length ? requestIds : undefined,
      all_candidates: allCandidates,
      limit,
    });
    return response.data;
  },

  regenerateMwl: async (requestId) => {
    const response = await api.post(`/requests/${requestId}/mwl/regenerate`);
    return response.data;
  },

  /**
   * Simulate an Orthanc result for a request and send the EMR callback.
   */
  simulateOrthancResult: async (requestId, data = {}) => {
    const response = await api.post(`/requests/${requestId}/simulate-orthanc-result`, data);
    return response.data;
  },

  /**
   * Build request success metrics from status totals
   */
  getSuccessMetrics: async () => {
    const [all, completed, failed, cancelled] = await Promise.all([
      requestService.getAll({ page: 1, page_size: 1 }),
      requestService.getAll({ page: 1, page_size: 1, status: 'completed' }),
      requestService.getAll({ page: 1, page_size: 1, status: 'failed' }),
      requestService.getAll({ page: 1, page_size: 1, status: 'cancelled' }),
    ]);

    const total = all?.total || 0;
    const completedCount = completed?.total || 0;
    const failedCount = failed?.total || 0;
    const cancelledCount = cancelled?.total || 0;
    const terminalCount = completedCount + failedCount + cancelledCount;
    const successRate = terminalCount > 0 ? (completedCount / terminalCount) * 100 : 0;

    return {
      total,
      completed: completedCount,
      failed: failedCount,
      cancelled: cancelledCount,
      terminal: terminalCount,
      successRate,
    };
  },
};

// Settings API calls
export const settingsService = {
  getErrorLogs: async ({ resource, user, client_ip, calling_url, start_time, end_time, page = 1, page_size = 50 } = {}) => {
    const response = await api.get('/settings/error-logs', {
      params: { resource, user, client_ip, calling_url, start_time, end_time, page, page_size },
    });
    return response.data;
  },

  getLoginHistory: async ({ user, calling_url, result, start_time, end_time, page = 1, page_size = 50 } = {}) => {
    const response = await api.get('/settings/login-history', {
      params: { user, calling_url, result, start_time, end_time, page, page_size },
    });
    return response.data;
  },

  getShaVerificationLogs: async ({ outcome, endpoint, search, user, start_time, end_time, page = 1, page_size = 50 } = {}) => {
    const response = await api.get('/settings/sha-verification-logs', {
      params: { outcome, endpoint, search, user, start_time, end_time, page, page_size },
    });
    return response.data;
  },

  // All calls matching the filters as a CSV blob.
  exportShaVerificationLogs: async ({ outcome, endpoint, search, user, start_time, end_time } = {}) => {
    const response = await api.get('/settings/sha-verification-logs/export', {
      params: { outcome, endpoint, search, user, start_time, end_time },
      responseType: 'blob',
    });
    return response.data;
  },

  // Public equipment usage access code: status only, the code is never returned.
  getPublicUsageAccess: async () => {
    const response = await api.get('/settings/public-usage-access');
    return response.data;
  },

  setPublicUsageAccess: async (accessCode) => {
    const response = await api.put('/settings/public-usage-access', { access_code: accessCode });
    return response.data;
  },

  clearPublicUsageAccess: async () => {
    const response = await api.delete('/settings/public-usage-access');
    return response.data;
  },

  getRevenueDistributions: async () => {
    const response = await api.get('/settings/revenue-distributions');
    return response.data;
  },

  createRevenueDistribution: async (data) => {
    const response = await api.post('/settings/revenue-distributions', data);
    return response.data;
  },

  updateRevenueDistribution: async (id, data) => {
    const response = await api.put(`/settings/revenue-distributions/${id}`, data);
    return response.data;
  },

  getUsers: async ({ page = 1, page_size = 20, is_active, search } = {}) => {
    const response = await api.get('/admin/users', {
      params: { page, page_size, is_active, search },
    });
    return response.data;
  },

  createUser: async (data) => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  updateUser: async (id, data) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  getPermissions: async ({ page = 1, page_size = 100, is_active = true, search } = {}) => {
    const response = await api.get('/admin/permissions', {
      params: { page, page_size, is_active, search },
    });
    return response.data;
  },

  assignUserPermission: async (userId, permissionId) => {
    const response = await api.post(`/admin/users/${userId}/permissions/${permissionId}`, {});
    return response.data;
  },

  unassignUserPermission: async (userId, permissionId) => {
    const response = await api.delete(`/admin/users/${userId}/permissions/${permissionId}`);
    return response.data;
  },
};

// Report API calls
export const reportService = {
  /**
   * Get procedure cost report rows.
   */
  getProcedureCostReport: async ({ vendor, facility, equipment, modality, procedure, status, start_date, end_date } = {}) => {
    const response = await api.get('/analytics/reports/procedure-costs', {
      params: { vendor, facility, equipment, modality, procedure, status, start_date, end_date },
    });
    return response.data;
  },

  /**
   * Download procedure cost report as CSV.
   */
  downloadProcedureCostReportCsv: async ({ vendor, facility, equipment, modality, procedure, status, start_date, end_date } = {}) => {
    const response = await api.get('/analytics/reports/procedure-costs/export', {
      params: { vendor, facility, equipment, modality, procedure, status, start_date, end_date },
      responseType: 'blob',
    });
    return response.data;
  },
};

// SHA API calls
export const shaService = {
  /**
   * List SHA intervention rows exposed by the backend.
   */
  getInterventions: async ({ patient_id, id_number, emr_request_id, facility_id, internal_request_id } = {}) => {
    const response = await api.get('/sha/interventions', {
      params: { patient_id, id_number, emr_request_id, facility_id, internal_request_id },
    });
    return response.data;
  },

  /**
   * Verify a claim through the selected source.
   */
  verifyClaimV2: async ({ claim_id, source }) => {
    const response = await api.post('/sha/v2/verification', { claim_id, source });
    return response.data;
  },
};

// DICOM error log (Orthanc, router, MPPS service, middleware)
export const dicomErrorService = {
  list: async (params = {}) => {
    const response = await api.get('/dicom/errors', { params });
    return response.data;
  },

  listForEquipment: async (equipmentId, params = {}) => {
    const response = await api.get(`/equipment/${equipmentId}/dicom-errors`, { params });
    return response.data;
  },

  acknowledge: async (id) => {
    const response = await api.post(`/dicom/errors/${id}/acknowledge`);
    return response.data;
  },
};

// Studies received by Orthanc and their match to requests/worklists
export const receivedStudyService = {
  list: async (params = {}) => {
    const response = await api.get('/dicom/received-studies', { params });
    return response.data;
  },

  // Short-lived OHIF viewer link for one study.
  viewerLink: async (studyId) => {
    const response = await api.get(`/dicom/received-studies/${studyId}/viewer-link`);
    return response.data;
  },

  // Counts per match status for the same filters, plus facilities that have sent studies.
  summary: async (params = {}) => {
    const response = await api.get('/dicom/received-studies/summary', { params });
    return response.data;
  },

  // Delete studies and their images in Orthanc; returns { deleted, not_found, failed }.
  deleteMany: async (ids) => {
    const response = await api.post('/dicom/received-studies/delete', { ids });
    return response.data;
  },
};

// Worklists published / pulled / completed per equipment
export const equipmentUsageService = {
  get: async (params = {}) => {
    const response = await api.get('/analytics/equipment-usage', { params });
    return response.data;
  },

  // Public page: guarded by an access code instead of a staff login.
  getPublic: async (accessCode, params = {}) => {
    const response = await api.get('/public/equipment-usage', {
      params,
      headers: { 'X-Access-Code': accessCode },
    });
    return response.data;
  },
};

// Dashboard: request funnel, amounts and equipment footprint for a date range
export const dashboardService = {
  get: async (params = {}) => {
    const response = await api.get('/analytics/dashboard', { params });
    return response.data;
  },
};
